/**
 * Redux Store Configuration
 *
 * This file sets up the Redux store for the POS application using Redux Toolkit.
 * It includes the API slice reducer and middleware, and enables Redux DevTools in non-production environments.
 *
 * Exports:
 * - store: The configured Redux store instance
 * - RootState: Type for the root state of the store
 * - AppDispatch: Type for the store's dispatch function
 */

import { configureStore } from "@reduxjs/toolkit";
import { apiSlice } from "./Features/api/apiSlice";

import assignmentSlice from "./Features/states/assignments/assignmentSlice";
import authSlice from "./Features/states/auth-management/authSlice";
import businessApplicationsSlice from "./Features/states/business-applications/businessApplicationsSlice";
import businessListSlice from "./Features/states/business-list/businessListSlice";
import businessOnboardingSlice from "./Features/states/business-onboarding/businessOnboardingSlice";
import dashboardSlice from "./Features/states/dashboard/dashboardSlice";
import centralProductCatalogSlice from "./Features/states/product-catalog/centralProductCatalogSlice";
import productRequestsSlice from "./Features/states/product-requests/productRequestsSlice";
import productSlice from "./Features/states/products/productSlice";
import aiModelsSlice from "./Features/states/settings/aiModelsSlice";
import emailCredentialsSlice from "./Features/states/settings/emailCredentialsSlice";
import staffBonusSlice from "./Features/states/staff-management/staffBonusSlice";
import staffSalarySlice from "./Features/states/staff-management/staffSalarySlice";
import stuffManagementSlice from "./Features/states/staff-management/stuffMembersSlice";
import tenantsSlice from "./Features/states/tenants/tenantsSlice";
import tenantsAssignmentSlice from "./Features/states/tenants-assignment/tenantsAssignmentSlice";

export const store = configureStore({
  reducer: {
    // Focused slices for better maintainability
    auth: authSlice,
    businessApplications: businessApplicationsSlice,
    businessList: businessListSlice,
    businessOnboarding: businessOnboardingSlice,
    centralProductCatalog: centralProductCatalogSlice,
    dashboard: dashboardSlice,
    stuffMembers: stuffManagementSlice,
    staffSalary: staffSalarySlice,
    staffBonus: staffBonusSlice,
    tenants: tenantsSlice,
    products: productSlice,
    assignments: assignmentSlice,
    tenantsAssignment: tenantsAssignmentSlice,
    aiModels: aiModelsSlice,
    emailCredentials: emailCredentialsSlice,
    productRequests: productRequestsSlice,
    // API slice reducer
    [apiSlice.reducerPath]: apiSlice.reducer,
  },
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddlewares) =>
    getDefaultMiddlewares().concat(apiSlice.middleware),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
